import { Container, Typography } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router";
import { useChatApiClient } from "../api/apiClients";
import { DashboardLayout } from "../components/DashboardLayout/DashboardLayout";
import { InternalError } from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import ChatTitle from "../components/PageHeader/ChatHeader";
import ChatInput from "../components/Sirius/ChatInput";
import MessageList from "../components/Sirius/MessageList";
import {
  useSendFeedback,
  useSendMessage,
} from "../components/Sirius/SiriusQueries";
import { BotChat, FeedbackThumbs, Message } from "../interfaces/chabot";

function ChatPage() {
  let params = useParams();
  const { state } = useLocation();
  const message = state?.message;
  const bot = state?.bot;

  const apiClient = useChatApiClient<BotChat>();
  const chatId = params.chatId as string;
  const {
    data: chat,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ["chat", chatId],
    queryFn: () => apiClient("GET", `/chat/${chatId}`),
    placeholderData: keepPreviousData,
    initialData:
      message && bot
        ? {
            id: "chatId",
            title: "Nouvelle conversation",
            favourite: false,
            updated_at: new Date(),
            messages: [
              {
                id: "temp",
                content: message.content,
                type: "human",
                insertion_date: new Date(),
              },
            ],
            bot: {
              id: bot.id,
              name: bot.name,
            },
          }
        : undefined,
    staleTime: 2000,
    initialDataUpdatedAt: message ? message.updatedAt : undefined,
  });

  const { mutate: sendMessage, isPending: awaitingResponse } =
    useSendMessage(chatId);
  const { mutate: feedbackMutate } = useSendFeedback(chatId);

  const sendFeedback = (message: Message, feedback: FeedbackThumbs) => {
    feedbackMutate({
      messageId: message.id,
      feedback: feedback === message.feedback ? null : feedback,
    });
  };

  function getContent() {
    if (isError) {
      return <InternalError />;
    } else if (isLoading) {
      return <Loading />;
    } else if (!!chat) {
      return (
        <Container
          maxWidth={false}
          className="flex grow w-full flex-col overflow-hidden p-0 items-center"
        >
          <MessageList
            messages={chat.messages}
            handleFeedback={sendFeedback}
            loading={awaitingResponse}
          />
          <div className="flex flex-col w-full px-4 lg:max-w-3xl xl:max-w-4xl lg:px-0">
            {!chat.original_chat_id && (
              <ChatInput
                chatId={chat.id}
                loading={awaitingResponse}
                sendMessage={sendMessage}
              />
            )}
            <Typography className="w-full p-2 text-center text-xs">
              Notre chatbot peut faire des erreurs. Pensez à vérifier les
              informations importantes
            </Typography>
          </div>
        </Container>
      );
    } else {
      return <InternalError />;
    }
  }

  return (
    <DashboardLayout>
      <Container
        maxWidth={false}
        className="flex h-full w-full flex-col overflow-hidden p-0"
      >
        <ChatTitle chat={chat} />
        {getContent()}
      </Container>
    </DashboardLayout>
  );
}

export default ChatPage;
